import { render, act, waitFor } from '@testing-library/react';
import userEvent from '@testing-library/user-event';
import React from 'react';
import CurrentAccess from './CurrentAccess';

// Mock the hooks and services
jest.mock('../hooks/useUsers');
jest.mock('../services/ssoAssignmentService');

const mockUseUsers = require('../hooks/useUsers').default;
const mockAssignSsoProvider = require('../services/ssoAssignmentService')
  .default;

describe('CurrentAccess', () => {
  const renderCurrentAccess = (props) => render(<CurrentAccess {...props} />);

  const defaultProps = {
    dealerId: 9999,
    isGlobalAdmin: true,
    csrfToken: 'test-csrf-token',
    isFleet: false,
    availableSsoProviders: {
      providers: ['hinob2c', 'ehisso', 'paccar'],
      require_sso: false,
    },
    ssoFeatureEnabled: true,
  };

  const mockUsers = [
    {
      id: 10001,
      username: 'john.doe',
      name: 'John Doe',
      job_role: 'Manager',
      permissions: ['Manager', 'Technician'],
      last_login_at: '01/22/2025 10:51 AM EST',
      show_warning: false,
      sso_access: {
        provider: 'hinob2c',
        assignment: 'manual',
      },
    },
    {
      id: 10002,
      username: 'jane.smith',
      name: 'Jane Smith',
      job_role: 'Technician',
      permissions: ['Technician'],
      last_login_at: '01/15/2025 02:30 PM EST',
      show_warning: true,
      sso_access: null,
    },
  ];

  const mockPagination = {
    perPage: 20,
    totalItems: 60,
    totalPages: 3,
  };

  beforeEach(() => {
    // Mock fetch for the become functionality
    global.fetch = jest.fn().mockResolvedValue({
      ok: true,
      url: '/dashboard',
    });

    // Mock the useUsers hook
    mockUseUsers.mockReturnValue({
      users: mockUsers,
      setUsers: jest.fn(),
      isLoading: false,
      pagination: mockPagination,
      fetchUsersData: jest.fn(),
    });

    // Mock the SSO assignment service
    mockAssignSsoProvider.mockResolvedValue({
      success: true,
      message: 'SSO provider assigned successfully',
      sso_access: {
        provider: 'ehisso',
        assignment: 'manual',
      },
    });
  });

  afterEach(() => {
    jest.restoreAllMocks();
  });

  describe('when component is loading', () => {
    beforeEach(() => {
      mockUseUsers.mockReturnValue({
        users: [],
        setUsers: jest.fn(),
        isLoading: true,
        pagination: mockPagination,
        fetchUsersData: jest.fn(),
      });
    });

    it('renders the loading state', () => {
      let queryByText;

      act(() => {
        ({ queryByText } = renderCurrentAccess(defaultProps));
      });

      expect(queryByText('Current Access')).toBeInTheDocument();
      expect(queryByText(/loading/i)).toBeInTheDocument();
    });

    it('calls fetchUsersData with correct parameters', () => {
      const mockFetchUsersData = jest.fn();
      mockUseUsers.mockReturnValue({
        users: [],
        setUsers: jest.fn(),
        isLoading: true,
        pagination: mockPagination,
        fetchUsersData: mockFetchUsersData,
      });

      act(() => {
        renderCurrentAccess(defaultProps);
      });

      expect(mockFetchUsersData).toHaveBeenCalledWith({
        page: 1,
        sortBy: null,
        sortOrder: null,
      });
    });
  });

  describe('after component has loaded', () => {
    describe('when there are users', () => {
      it('renders the users table', async () => {
        let container;

        act(() => {
          ({ container } = renderCurrentAccess(defaultProps));
        });

        await waitFor(() => {
          expect(container.querySelector('table')).toBeInTheDocument();
        });

        // Verify table structure exists instead of snapshot
        expect(container.querySelector('thead')).toBeInTheDocument();
        expect(container.querySelector('tbody')).toBeInTheDocument();
      });

      it('displays user information correctly', async () => {
        let getByText;

        act(() => {
          ({ getByText } = renderCurrentAccess(defaultProps));
        });

        await waitFor(() => {
          expect(getByText('john.doe')).toBeInTheDocument();
          expect(getByText('John Doe')).toBeInTheDocument();
          expect(getByText('Manager')).toBeInTheDocument();
          expect(getByText('01/22/2025 10:51 AM EST')).toBeInTheDocument();
        });
      });

      it('renders SSO access cells with correct providers', async () => {
        let getByText;

        act(() => {
          ({ getByText } = renderCurrentAccess(defaultProps));
        });

        await waitFor(() => {
          expect(getByText('hinob2c')).toBeInTheDocument();
          expect(getByText('Unassigned')).toBeInTheDocument();
        });
      });

      describe('when user has dealer administrator permission in fleet context', () => {
        it('displays Fleet Administrator instead of Dealer Administrator', async () => {
          const fleetProps = {
            ...defaultProps,
            isFleet: true,
          };

          const usersWithDealerAdmin = [
            {
              ...mockUsers[0],
              permissions: ['Dealer Administrator', 'Manager'],
            },
          ];

          mockUseUsers.mockReturnValue({
            users: usersWithDealerAdmin,
            setUsers: jest.fn(),
            isLoading: false,
            pagination: mockPagination,
            fetchUsersData: jest.fn(),
          });

          let getByText;

          act(() => {
            ({ getByText } = renderCurrentAccess(fleetProps));
          });

          await waitFor(() => {
            expect(
              getByText('Fleet Administrator, Manager'),
            ).toBeInTheDocument();
          });
        });
      });

      describe('when user clicks edit button', () => {
        it('navigates to user edit page', async () => {
          let getAllByLabelText;

          // Mock window.location.href
          delete window.location;
          window.location = { href: '' };

          act(() => {
            ({ getAllByLabelText } = renderCurrentAccess(defaultProps));
          });

          await waitFor(() => {
            const editButtons = getAllByLabelText('Edit');
            expect(editButtons.length).toBeGreaterThan(0);
          });

          const editButtons = getAllByLabelText('Edit');
          userEvent.click(editButtons[0]);

          expect(window.location.href).toBe('/users/edit/10001');
        });
      });

      describe('when global admin clicks become button', () => {
        it('calls the become API and redirects', async () => {
          let getAllByText;

          act(() => {
            ({ getAllByText } = renderCurrentAccess(defaultProps));
          });

          await waitFor(() => {
            const becomeButtons = getAllByText('BECOME');
            expect(becomeButtons.length).toBeGreaterThan(0);
          });

          const becomeButtons = getAllByText('BECOME');
          userEvent.click(becomeButtons[0]);

          expect(fetch).toHaveBeenCalledWith('/users/10001/become', {
            method: 'POST',
            headers: {
              'X-CSRF-Token': 'test-csrf-token',
            },
          });
        });
      });

      describe('when non-global admin', () => {
        it('does not show become buttons', async () => {
          const nonAdminProps = {
            ...defaultProps,
            isGlobalAdmin: false,
          };

          let queryByText;

          act(() => {
            ({ queryByText } = renderCurrentAccess(nonAdminProps));
          });

          await waitFor(() => {
            expect(queryByText('BECOME')).not.toBeInTheDocument();
          });
        });
      });

      describe('when SSO assignment is changed', () => {
        it('updates the user state correctly', async () => {
          const mockSetUsers = jest.fn();
          mockUseUsers.mockReturnValue({
            users: mockUsers,
            setUsers: mockSetUsers,
            isLoading: false,
            pagination: mockPagination,
            fetchUsersData: jest.fn(),
          });

          let container;

          act(() => {
            ({ container } = renderCurrentAccess(defaultProps));
          });

          await waitFor(() => {
            expect(container.querySelector('table')).toBeInTheDocument();
          });

          // Simulate SSO assignment change by verifying the function exists
          expect(mockSetUsers).toBeDefined();
        });
      });
    });

    describe('when there are no users', () => {
      beforeEach(() => {
        mockUseUsers.mockReturnValue({
          users: [],
          setUsers: jest.fn(),
          isLoading: false,
          pagination: { perPage: 20, totalItems: 0, totalPages: 0 },
          fetchUsersData: jest.fn(),
        });
      });

      it('renders the empty state component', async () => {
        let getByText;

        act(() => {
          ({ getByText } = renderCurrentAccess(defaultProps));
        });

        await waitFor(() => {
          expect(getByText('No Users Added Yet')).toBeInTheDocument();
        });
      });

      it('still renders the header', async () => {
        let getByText;

        act(() => {
          ({ getByText } = renderCurrentAccess(defaultProps));
        });

        await waitFor(() => {
          expect(getByText('Current Access')).toBeInTheDocument();
        });
      });
    });

    describe('warning system', () => {
      it('shows header when users have warnings', async () => {
        let container;

        act(() => {
          ({ container } = renderCurrentAccess(defaultProps));
        });

        await waitFor(() => {
          const headerSection = container.querySelector('h2');
          expect(headerSection).toBeInTheDocument();
        });
      });
    });
  });

  describe('SSO provider integration', () => {
    it('passes available SSO providers to component', async () => {
      let container;

      act(() => {
        ({ container } = renderCurrentAccess(defaultProps));
      });

      await waitFor(() => {
        expect(container.querySelector('table')).toBeInTheDocument();
      });
    });

    it('handles empty SSO providers gracefully', async () => {
      const propsWithoutSso = {
        ...defaultProps,
        availableSsoProviders: {
          providers: [],
          require_sso: false,
        },
      };

      let container;

      act(() => {
        ({ container } = renderCurrentAccess(propsWithoutSso));
      });

      await waitFor(() => {
        expect(container.querySelector('table')).toBeInTheDocument();
      });

      // Verify component renders correctly with empty providers
      expect(container.querySelector('thead')).toBeInTheDocument();
      expect(container.querySelector('tbody')).toBeInTheDocument();
    });
  });
});
